<script>
  import { External, M } from '../../external';
  import { onDestroy, onMount } from 'svelte';
  import { LmsUtils } from '../utils/LmsUtils';
  import {
    addGroup,
    addPage,
    setCircleSettings,
    setCircleStyling,
    showCircleStyling,
    showPageSettings,
    showSettingsPopup
  } from '../service/LmsUIService';
  import { ignore } from '../../Common/tools';
  import { makeLmsRequest } from '../service/LmsClient';
  import CircleSettings from '../popups/CircleSettings.svelte';
  import { LmsCircleUtils } from './LmsCircleUtils';
  import MenuItem from './MenuItem.svelte';
  import PageGroupSettings from '../popups/PageGroupSettings.svelte';
  import CirclePageDetails from './CirclePageDetails.svelte';
  import { fly } from 'svelte/transition';
  import LmsCircleTopMenu from './LmsCircleTopMenu.svelte';
  import LmsCircleSidebar from './LmsCircleSidebar.svelte';
  import GroupPicker from '../popups/GroupPicker.svelte';
import {
  applyLocalizedFields,
  buildLocalizedPayload,
  CIRCLE_LOCALIZED_FIELDS,
  GROUP_LOCALIZED_FIELDS,
  PAGE_LOCALIZED_FIELDS
} from '../utils/LmsLocaleUtils';

  export let
    onLoad = ignore,
    inPopup = false;

  export async function savePage(showConfirmationMessage = true) {
    await savePageChanges(showConfirmationMessage);
  }

  let loading = true;
  let totalPagesWithEnabledTracking = 0;
  let circleDetails;
  let pageDetailsInstance = null;
  let localeSettings = { locales: [], defaultLocale: 'en', currentLocale: 'en' };

  let pageChanged = false;
  let historyButtonsVisible = false;
  let savingPage = false;

  let menuSize = 'wide';

  let orderedItems = [];
  let selectedPage;
  let selectedGroup = null;
  let circleShadow = '';
  let circleImage = 'none';
  let isTemplate = false;

  let save = async () => {
    let payload = buildLocalizedPayload(circleDetails.circle, CIRCLE_LOCALIZED_FIELDS);
    let resp = await makeLmsRequest('/circle/' + circleDetails.circle.id, 'PUT', payload);
    if (resp.ok) {
      circleDetails.circle = await resp.json();
      applyLocalizedFields(circleDetails.circle, CIRCLE_LOCALIZED_FIELDS);
      setCircleSettings(circleDetails.circle);
      setCircleStyling(circleDetails.circle);
      LmsUtils.updateCachedCircle(circleDetails.circle);
    }
  };

  let toggleMenu = () => {
    menuSize = menuSize === 'compact' ? 'wide' : 'compact';
  };

  let selectPage = (page, ignoreChanges) => {
    if (selectedPage === page) {
      return;
    }
    if (pageChanged && !ignoreChanges) {
      External.CPanel.showUnsavedChangesPrompt(() => {
        setPageChanged(false, false);
        selectPage(page, true);
      }, ignore);
    } else {
      setPageChanged(false, false);
      selectedPage = page;
      if (!page.groupId) {
        selectedGroup = null;
      } else if (!selectedGroup || selectedGroup.pageGroup.id !== page.groupId) {
        selectedGroup = circleDetails.groups.find(group => group.pageGroup.id === page.groupId) || null;
      }
    }
  };

  let getCircleId = () => {
    const DELIMITER = 'circles/';
    const url = window.location.pathname;
    let path = url.substring(url.indexOf(DELIMITER) + DELIMITER.length);
    let parts = path.split('/');
    let circleUrl = parts[0];
    let templateId = '';
    if (circleUrl === 'edit-template') {
      templateId = External.WebPlatform.getUrlQueryParameter(window.location.href, 'id');
    }
    return {
      isTemplate: !!templateId,
      id: templateId || circleUrl
    };
  };

  let getFirstAvailablePage = () => {
    for (let idx = 0; idx < orderedItems.length; ++idx) {
      let item = orderedItems[idx];
      if (item.pageGroup) {
        if (item.pages?.length) {
          return item.pages[0];
        }
      } else {
        return item;
      }
    }
    return null;
  };

  async function load() {
    loading = true;
    let circleId = getCircleId();
    isTemplate = circleId.isTemplate;
    if (isTemplate) {
      circleDetails = await LmsUtils.getCircleTemplate(circleId.id);
    } else {
      circleDetails = await LmsUtils.getCircleDetailsByUrl(circleId.id);
    }
    setCircleSettings(circleDetails.circle);
    setCircleStyling(circleDetails.circle);
    menuSize = circleDetails.circle.styling.menuSize;
    setCircleWrapperBoxShadow();
    setCircleImage();
    refreshPageOrder();

    let page = null;
    if (circleId.pageUrl) {
      page = LmsUtils.getByPageUrl(circleDetails, circleId.pageUrl);
    }

    if (!page) {
      page = getFirstAvailablePage();
    }

    if (page) {
      selectPage(page, true);
    }

    calculateTotalPagesWithTrackingProgressEnabled();
    loading = false;
    onLoad();
  }

  let calculateTotalPagesWithTrackingProgressEnabled = () => {
    totalPagesWithEnabledTracking = LmsUtils.getPagesWithTrackingProgressEnabled(circleDetails).length;
  };

  let refreshPageOrder = () => {
    orderedItems = LmsUtils.getPagesAndGroupsInOrder(circleDetails);
  };

  let onStylesChanged = () => {
    circleDetails.circle.styling = circleDetails.circle.styling;
    menuSize = circleDetails.circle.styling.menuSize;
    setCircleWrapperBoxShadow();
    setCircleImage();
  };

  let showCircleStylingFromCircle = () => {
    showCircleStyling(circleDetails.circle, save, onStylesChanged);
  };

  let showCircleSettings = () => {
    let changed = false;
    showSettingsPopup({
      width: 500,
      component: (target) =>
        new CircleSettings({
          target: target,
          props: {
            circle: circleDetails.circle,
            isTemplate: isTemplate,
            onChange: (stylingChanged) => {
              changed = true;
              if (stylingChanged) {
                onStylesChanged();
              }
            }
          }
        }),
      onHide: () => {
        if (changed) {
          save();
        }
      }
    });
  };

  let addPageFromCircle = (group) => {
    let uiOrder;
    if (group) {
      uiOrder = group.pages.length ? group.pages[group.pages.length - 1].uiOrder + 100 : 100;
    } else {
      uiOrder = getMaxUiOrder() + 100;
    }
    addPage(circleDetails.circle.id, group ? group.pageGroup.id : null, uiOrder, (newPage) => {
      applyLocalizedFields(newPage, PAGE_LOCALIZED_FIELDS);
      if (group) {
        group.pages.push(newPage);
      } else {
        circleDetails.pages.push(newPage);
      }
      selectedGroup = selectedGroup;
      refreshPageOrder();
      selectPage(newPage);
      showPageSettingsFromCircle(newPage);
    });
  };

  let showGroupSettings = (group) => {
    let changed = false;
    External.Tools.showWindow({
      isModal: false,
      withDarkBackground: true,
      width: 520,
      buttons: [],
      noNav: true,
      noTitle: true,
      forceAtRight: window.showBuilderPopupsOnFullHeight,
      action: {
        onShow: (popup, ui) => {
          popup.bindComponent(
            new PageGroupSettings({
              target: popup.getContentArea(),
              props: {
                circleDetails: circleDetails,
                isTemplate: isTemplate,
                group: group,
                onChange: () => {
                  changed = true;
                }
              }
            })
          );
        },
        onHide: async () => {
          if (changed) {
            let payload = buildLocalizedPayload(group.pageGroup, GROUP_LOCALIZED_FIELDS);
            let result = await makeLmsRequest('/circle/' + group.pageGroup.circleId + '/group/' + group.pageGroup.id, 'PUT', payload);
            await result.json();
            orderedItems = orderedItems;
            selectedGroup = selectedGroup;
          }
        }
      }
    });
  };

  let showPageSettingsFromCircle = async (page) => {
    let changedPage = await showPageSettings(circleDetails, page, isTemplate);
    if (changedPage) {
      orderedItems = orderedItems;
      selectedGroup = selectedGroup;
      if (selectedPage && changedPage.id === selectedPage.id) {
        selectedPage.hidden = changedPage.hidden;
      }
    }
  };

  let publishActivePage = async () => {
    selectedPage.hidden = false;
    await makeLmsRequest('/page/' + selectedPage.id, 'PUT', buildLocalizedPayload(selectedPage, PAGE_LOCALIZED_FIELDS));
    External.Tools.showCornerMessage(1000, M.PAGE_PUBLISHED, External.Tools.TOOLTIP_TYPE_SUCCESS);
  };

  let movePageToGroup = async (page, newGroupId) => {
    if (newGroupId === page.groupId) {
      return;
    }

    let targetGroup = circleDetails.groups.find(group => group.pageGroup.id === newGroupId);
    let sourcePages = page.groupId ? circleDetails.groups.find(group => group.pageGroup.id === page.groupId).pages || [] : circleDetails.pages;
    let targetPages = newGroupId ? targetGroup.pages || [] : circleDetails.pages;

    let sourceIdx = sourcePages.findIndex(p => p.id === page.id);
    if (sourceIdx !== -1) {
      sourcePages.splice(sourceIdx, 1);
    }
    page.groupId = newGroupId;
    // let's put the page at the end
    if (newGroupId) {
      // easy for groups - just make it the last page order + 100
      page.uiOrder = targetPages.length ? targetPages[targetPages.length - 1].uiOrder + 100 : 100;
    } else {
      if (orderedItems.length) {
        // for root pages, we want to put it at the end - after all pages and groups
        let item = orderedItems[orderedItems.length - 1];
        page.uiOrder = (item.pageGroup ? item.pageGroup.uiOrder : item.uiOrder) + 100;
      } else {
        page.uiOrder = 100;
      }
    }
    targetPages.push(page);
    let result = await makeLmsRequest('/page/' + page.id, 'PUT', buildLocalizedPayload(page, PAGE_LOCALIZED_FIELDS));
    refreshPageOrder();
    selectedGroup = page.groupId ? targetGroup : null;
  };

  let showMoveToGroupDialog = (page) => {
    let newGroupId = page.groupId;
    External.Tools.showWindow({
      isModal: false,
      title: M.MOVE_TO_GROUP,
      width: 450,
      buttons: [
        External.Button(M.SUBMIT, External.Button.CLASS_MIDDLE_BLUE, function(popup) {
          movePageToGroup(page, newGroupId);
          popup.close();
        }),
        External.Button.MIDDLE_CANCEL
      ],
      action: {
        onShow: function(popup, ui) {
          popup.bindComponent(new GroupPicker({
            target: popup.getContentArea(),
            props: {
              selected: page.groupId,
              groups: circleDetails.groups,
              onChange: (groupId) => {
                newGroupId = groupId;
              }
            }
          }));
        }
      }
    });
  };

  let showPageOptions = (target, page) => {
    let hasGroups = circleDetails.groups.length > 0;

    const items = [
      {
        icon: 'fa-cog',
        text: M.PAGE_SETTINGS,
        tag: 'page_settings'
      },
      {
        icon: 'fa-clone',
        text: M.DUPLICATE,
        tag: 'duplicate'
      }, ...(hasGroups ? [
        {
          icon: 'fa-share',
          text: M.MOVE_TO_GROUP,
          tag: 'move'
        }] : []),
      {
        icon: 'fa-trash',
        text: M.DELETE,
        tag: 'delete'
      },
      ...(isTemplate ? [] : [{
        icon: 'fa-pie-chart',
        text: M.ANALYTICS,
        tag: 'analytics'
      }])
    ];

    External.Tools.showTooltipOptions(target, items, async (target, idx, text, tag) => {
      switch (tag) {
        case 'page_settings':
          showPageSettingsFromCircle(page);
          break;
        case 'duplicate':
          let clonedPage = await LmsCircleUtils.duplicatePage(circleDetails.circle, page);
          let isRootPage = circleDetails.pages.find(p => p.id === page.id);
          if (isRootPage) {
            circleDetails.pages = [...circleDetails.pages, clonedPage];
          } else {
            let groupHolder = circleDetails.groups.find(holder => holder.pageGroup.id === page.groupId);
            if (groupHolder) {
              groupHolder.pages = [...groupHolder.pages, clonedPage];
            }
          }
          refreshPageOrder();
          selectPage(clonedPage);
          break;
        case 'delete':
          External.Tools.prompt(M.DELETE, M.DELETE_PAGE_QUESTION, async () => {
            let result = await makeLmsRequest('/page/' + page.id, 'DELETE');
            if (result.ok) {
              circleDetails.pages = circleDetails.pages.filter(p => p.id !== page.id);
              circleDetails.groups.forEach(g => {
                g.pages = g.pages.filter(p => p.id !== page.id);
              });
              refreshPageOrder();
              if (page.id === selectedPage?.id) {
                let page = getFirstAvailablePage();
                selectPage(page, true);
              }
            }
          });
          break;
        case 'move':
          showMoveToGroupDialog(page);
          break;
        case 'analytics':
          LmsCircleUtils.showAnalytics(circleDetails, page, 'PAGE');
          break;
        default:
          break;
      }
    });
  };

  let showGroupOptions = (target, group) => {
    const items = [
      {
        icon: 'fa-cog',
        text: M.GROUP_SETTINGS,
        tag: 'group_settings'
      },
      {
        icon: 'fa-trash',
        text: M.DELETE,
        tag: 'delete'
      },
      ...(isTemplate ? [] : [{
        icon: 'fa-pie-chart',
        text: M.ANALYTICS,
        tag: 'analytics'
      }])
    ];

    External.Tools.showTooltipOptions(target, items, function(target, idx, text, tag) {
      switch (tag) {
        case 'group_settings':
          showGroupSettings(group);
          break;
        case 'delete':
          External.Tools.prompt(M.DELETE, M.DELETE_GROUP_QUESTION, async () => {
            if (group.pages.length === 0) {
              let result = await makeLmsRequest(['/circle/', circleDetails.circle.id, '/group/', group.pageGroup.id].join(''), 'DELETE');
              if (!result.ok) {
                return;
              }
              circleDetails.groups = circleDetails.groups.filter(g => g.pageGroup.id !== group.pageGroup.id);
              refreshPageOrder();
              if (selectedGroup && selectedGroup.pageGroup.id === group.pageGroup.id) {
                closeSelectedGroup();
              }
            } else {
              External.Tools.showError('LMS_GROUP_NOT_EMPTY');
            }
          });
          break;
        case 'analytics':
          LmsCircleUtils.showAnalytics(circleDetails, group.pageGroup, 'GROUP');
          break;
      }
    });
  };

  let onClickMenuItem = (item) => {
    if (item.pageGroup) {
      item.expanded = !item.expanded;
      orderedItems = orderedItems;
      switch (circleDetails.circle.styling.submenuStyle) {
        case 'sidebar':
          selectedGroup = selectedGroup === item ? null : item;
          break;
        case 'replace':
          selectedGroup = item;
          break;
      }
    } else {
      selectPage(item);
    }
  };

  let closeSelectedGroup = () => {
    selectedGroup = null;
  };

  let setPageChanged = (changed, keepHistoryButtons) => {
    savingPage = false;
    pageChanged = changed;
    historyButtonsVisible = keepHistoryButtons;
    External.CPanel.setChanged(pageChanged);
  };


  let discardPageChanges = () => {
    setPageChanged(false, false);
    if (pageDetailsInstance) {
      pageDetailsInstance.discard();
    }
  };

  let savePageChanges = async (showConfirmationMessage = true) => {
    if (savingPage || !pageChanged) {
      return;
    }
    savingPage = true;
    if (await pageDetailsInstance?.save(showConfirmationMessage)) {
      setPageChanged(false, true);
    }
    savingPage = false;
  };

  let undo = () => {
    if (pageDetailsInstance) {
      pageDetailsInstance.undo();
    }
  };

  let redo = () => {
    if (pageDetailsInstance) {
      pageDetailsInstance.redo();
    }
  };

  let getMaxUiOrder = () => {
    let pageOrder = circleDetails.pages.length ? circleDetails.pages[circleDetails.pages.length - 1].uiOrder : 0;
    let groupOrder = circleDetails.groups.length ? circleDetails.groups[circleDetails.groups.length - 1].pageGroup.uiOrder : 0;
    return Math.max(pageOrder, groupOrder);
  };

  let onAdd = (event) => {
    const items = [
      {
        icon: 'fa-circle-o',
        text: M.GROUP,
        tag: 'group'
      },
      {
        icon: 'fa-file-o',
        text: M.PAGE,
        tag: 'page'
      }
    ];

    External.Tools.showTooltipOptions(External.$(event.target), items, function(target, idx, text, tag) {
      switch (tag) {
        case 'group':
          addGroup(circleDetails.circle.id, getMaxUiOrder() + 100, (newGroup) => {
            applyLocalizedFields(newGroup, GROUP_LOCALIZED_FIELDS);
            let group = { pageGroup: newGroup, pages: [] };
            circleDetails.groups.push(group);
            refreshPageOrder();
            showGroupSettings(group);
          });
          break;
        case 'page':
          addPageFromCircle(null);
          break;
      }
    });
  };

  let setCircleImage = () => {
    let thumb = circleDetails.circle.styling.thumbnail.image;
    if (thumb?.name) {
      circleImage = 'url(' + External.getFileFromBase(thumb.base) + thumb.name + '?format=webp)';
    } else {
      circleImage = 'url(https://content.app-sources.com/s/56276073477626212/thumbnails/640x480/Images/1-9342609.jpg)';
    }
  };

  let setCircleWrapperBoxShadow = () => {
    if (circleDetails.circle.styling.addShadow) {
      circleShadow = External.UITools.getShadowValue(circleDetails.circle.styling.shadow);
    } else {
      circleShadow = '';
    }
  };

  let togglePublishedState = () => {
    let message = circleDetails.circle.published ? M.UNPUBLISH_CIRCLE_QUESTION : M.PUBLISH_CIRCLE_QUESTION;
    External.Tools.prompt('', message, async () => {
      await LmsCircleUtils.togglePublished(circleDetails.circle);
      circleDetails.circle = circleDetails.circle;
    });
  };

  let getLocaleSettings = () => {
    let settings = External.WebsiteSettings && External.WebsiteSettings.getSettings ? (External.WebsiteSettings.getSettings() || {}) : {};
    let defaultLocale = (settings.locale || (External.Website && External.Website.defaultEditLocale) || 'en').toString();
    let locales = Array.isArray(settings.websiteLocales) ? settings.websiteLocales.slice(0) : [];
    if (locales.indexOf(defaultLocale) < 0) {
      locales.unshift(defaultLocale);
    }
    let currentLocale = (window.WebPlatform && window.WebPlatform.getUrlQueryParameter ? window.WebPlatform.getUrlQueryParameter(window.location.href, 'locale') : '')
      || (External.Website && (External.Website.currentEditLocale || External.Website.defaultEditLocale))
      || defaultLocale;
    if (locales.indexOf(currentLocale) < 0) {
      currentLocale = defaultLocale;
    }
    return { locales, defaultLocale, currentLocale };
  };

  let changeLocale = (locale) => {
    if (!locale || locale === localeSettings.currentLocale) {
      return;
    }
    let go = () => {
      if (External.Website) {
        External.Website.currentEditLocale = locale;
      }
      if (window.Cookies && Cookies.set) {
        Cookies.set('current-locale', locale, { expires: 5 * 365, path: '/' });
      }
      let search = new URLSearchParams(window.location.search || '');
      search.set('locale', locale);
      window.location.href = window.location.pathname + '?' + search.toString() + (window.location.hash || '');
    };
    if (pageChanged) {
      External.CPanel.showUnsavedChangesPrompt(() => {
        setPageChanged(false, false);
        go();
      }, ignore);
    } else {
      go();
    }
  };

  onMount(async () => {
    Tools.addShortcutListener('s', savePageChanges);
    localeSettings = getLocaleSettings();
    await load();
  });

  onDestroy(() => {
    Tools.removeShortcutListener('s');
  });

  $: selectedGroupTrackedPages = selectedGroup ? selectedGroup.pages.filter(page => page.settings?.trackContentCompletion).length : 0;
</script>
<div class="admin-circle-wrapper col20 f-left flex flex-col full-size align-center">
  {#if loading}
    <!-- loading -->
  {:else}
    <LmsCircleTopMenu {isTemplate} {circleDetails} {pageChanged} {historyButtonsVisible} {savingPage}
                      locales={localeSettings.locales} defaultLocale={localeSettings.defaultLocale} currentLocale={localeSettings.currentLocale}
                      on:localechange={(event) => changeLocale(event.detail)}
                      on:settings={showCircleSettings}
                      on:styling={showCircleStylingFromCircle}
                      on:unpublish={togglePublishedState} on:undo={undo} on:redo={redo} on:save={savePageChanges} on:discard={discardPageChanges} />

    <div class="circle-wrapper flex flex-1 flex-row"
         class:rounded-circle={circleDetails.circle.styling.roundCorners}
         class:circle-layout-2={menuSize === 'compact'} class:circle-layout-1={menuSize === 'wide'}
         class:dark-ui-theme={circleDetails.circle.styling.darkTheme}
         style:max-height={circleDetails.circle.styling.fixToViewportHeight ? (circleDetails.circle.styling.viewportHeight) + 'vh' : 'none'}
         style:padding={(circleDetails.circle.styling.showCirclePadding ? circleDetails.circle.styling.circlePadding : 0) + 'px'}
         style:background={circleDetails.circle.styling.showCirclePadding ? circleDetails.circle.styling.bg?.color || '#fff' : '#fff'}
         style:--navigationWidth={circleDetails.circle.styling.navigationWidth}
         style:--circleCoverBackgroundColor={circleDetails.circle.styling.thumbnail.coverBg.color}
         style:--circleNameFont={circleDetails.circle.styling.textStyling?.circleNameFont}
         style:--circleNameTextSize={circleDetails.circle.styling.textStyling?.circleNameTextSize}
         style:--circleNameColor={circleDetails.circle.styling.textStyling?.nameFg?.color}

         style:--navigationBackgroundColor={circleDetails.circle.styling.navBg.color}
         style:--navTextFg={circleDetails.circle.styling.textStyling?.fg?.color}
         style:--iconBg={circleDetails.circle.styling.thumbnail.icon.bg.color}
         style:--iconFg={circleDetails.circle.styling.thumbnail.icon.fg.color}
         style:--menuIconFg={circleDetails.circle.styling.iconStyling.fg.color}
         style:--menuIconBg={circleDetails.circle.styling.iconStyling.bg.color}
         style:--menuIconBorder={circleDetails.circle.styling.iconStyling.border.color}
         style:--badgeFg={circleDetails.circle.styling.textStyling?.badgeFg?.color}
         style:--badgeBg={circleDetails.circle.styling.textStyling?.badgeBg?.color}
         style:--navPageFontFamily={circleDetails.circle.styling.textStyling?.pageNameFont || 'Arial'}
         style:--navPageFontSize={(circleDetails.circle.styling.textStyling?.pageNameTextSize || 14) + 'px'}

         style:--sidebarBgColor={circleDetails.circle.styling.sidebar.bg.color}
         style:--sidebarTextColor={circleDetails.circle.styling.sidebar.textStyling?.fg?.color}
         style:--sidebarIconFg={circleDetails.circle.styling.sidebar.iconStyling.fg.color}
         style:--sidebarIconBg={circleDetails.circle.styling.sidebar.iconStyling.bg.color}
         style:--sidebarIconBorder={circleDetails.circle.styling.sidebar.iconStyling.border.color}
         style:--sidebarBadgeFg={circleDetails.circle.styling.sidebar.textStyling?.badgeFg.color}
         style:--sidebarBadgeBg={circleDetails.circle.styling.sidebar.textStyling?.badgeBg.color}
         style="{circleShadow}">

      <div class="circle-navigation relative flex flex-row">
        {#if circleDetails.circle.styling.hasExpandButton}
          <span class="expand-collapse-navigation-btn rounded-effect" class:rotate={menuSize === 'wide'} on:click={toggleMenu}>
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#5f6368"><path
              d="m600-200-57-56 184-184H80v-80h647L544-704l56-56 280 280-280 280Z" /></svg>
          </span>
        {/if}

        <div class="circle-sidebar custom-scroll" class:hidden-profile-picture={circleDetails.circle.styling.thumbnail.type === 'none'}>
          <div class="circle-cover circle-cover-sidebar col20 f-left"
               class:small={circleDetails.circle.styling.thumbnail.size === 'small'}
               class:medium={circleDetails.circle.styling.thumbnail.size === 'medium'}>
            {#if circleDetails.circle.styling.hasProfileButton}
              <a class="circle-profile-btn rounded-effect">
                <svg xmlns="http://www.w3.org/2000/svg" enable-background="new 0 0 24 24" height="24px" viewBox="0 0 24 24" width="24px"
                     fill="#000000">
                  <g>
                    <rect fill="none" height="24" width="24"></rect>
                  </g>
                  <g>
                    <path
                      d="M12,2C6.48,2,2,6.48,2,12s4.48,10,10,10s10-4.48,10-10S17.52,2,12,2z M7.35,18.5C8.66,17.56,10.26,17,12,17 s3.34,0.56,4.65,1.5C15.34,19.44,13.74,20,12,20S8.66,19.44,7.35,18.5z M18.14,17.12L18.14,17.12C16.45,15.8,14.32,15,12,15 s-4.45,0.8-6.14,2.12l0,0C4.7,15.73,4,13.95,4,12c0-4.42,3.58-8,8-8s8,3.58,8,8C20,13.95,19.3,15.73,18.14,17.12z"></path>
                    <path
                      d="M12,6c-1.93,0-3.5,1.57-3.5,3.5S10.07,13,12,13s3.5-1.57,3.5-3.5S13.93,6,12,6z M12,11c-0.83,0-1.5-0.67-1.5-1.5 S11.17,8,12,8s1.5,0.67,1.5,1.5S12.83,11,12,11z"></path>
                  </g>
                </svg>
              </a>
            {/if}

            {#if circleDetails.circle.settings.showCircleAvatar && circleDetails.circle.styling.thumbnail.type !== 'none'}
              <div class="col20 f-left circle-image-outer flex justify-center"
                   class:small={circleDetails.circle.styling.thumbnail.size === 'small'}
                   class:medium={circleDetails.circle.styling.thumbnail.size === 'medium'}>
                {#if circleDetails.circle.styling.thumbnail.type === 'image'}
                  <div style:background-image={circleImage} class="circle-image"></div>
                {:else}
                  <div class="circle-image circle-image-icon">
                    {#if circleDetails.circle.styling.thumbnail.icon.value?.length > 20}
                      {@html circleDetails.circle.styling.thumbnail.icon.value}
                    {:else}
                      <span class="material-icons-outlined">{circleDetails.circle.styling.thumbnail.icon.value}</span>
                    {/if}
                  </div>
                {/if}
              </div>
            {/if}

            <div class="col20 f-left flex justify-center mt-15 flex-col circle-sidebar-header">
              <h2 class="circle-name col20 f-left">{circleDetails.circle.name}</h2>
              {#if circleDetails.circle.settings.showCircleMember && circleDetails.circle.settings.showCircleMemberCountInNavigation}
              <span class="mt-10 col20 f-left flex align-center justify-center">
                <span class="circle-members-counter">{circleDetails.circle.members} {circleDetails.circle.members === 1 ? M.MEMBER : M.MEMBERS}</span>
              </span>
              {/if}
              {#if circleDetails.circle.settings.showDescription && circleDetails.circle.description}
                <span class="circle-description col20 f-left">{circleDetails.circle.description}</span>
              {/if}
              {#if circleDetails.circle.settings.showTrackingProgress}
                <span class="col20 f-left mt-10 flex align-center justify-center">
                  <span class="circle-completion-info">0/{totalPagesWithEnabledTracking} {M.COMPLETED}</span>
                </span>
              {/if}
            </div>

          </div>
          

          <div class="col20 f-left circle-sidebar-content-wrapper" style="padding: 0px"
               class:icons-with-border={circleDetails.circle.styling.iconStyling.hasBorder}>

            <div class="col20 f-left relative">
              <div class="col20 f-left circle-root-menu-wrapper"
                   style:visibility="{selectedGroup && circleDetails.circle.styling.submenuStyle === 'replace' ? 'hidden' : 'visible'}"
                   use:LmsUtils.setupMenuSortable={[circleDetails.circle, orderedItems, () => { orderedItems = orderedItems; }]}>
                {#each orderedItems as item, index (item)}
                  {#if item.pageGroup}
                    <div class="col20 flex align-center flex-col circle-group-wrapper">
                      {#key item}
                        <MenuItem circleDetails={circleDetails} index={LmsUtils.getMenuItemNumber(item.pageGroup, orderedItems)} item={item.pageGroup}
                                  group={item} on:click={() => onClickMenuItem(item)}
                                  active={selectedGroup === item}
                                  {menuSize}
                                  on:options={(event) => showGroupOptions(event.detail.target, item)} />
                      {/key}
                      {#if circleDetails.circle.styling.submenuStyle === 'inline'}
                        {#if item.expanded}
                          <div class="circle-group-navigation col20 f-left"
                               use:LmsUtils.setupMenuSortable={[circleDetails.circle, item.pages, () => { item.pages = item.pages }]}>
                            {#each item.pages as page, pageIdx (page)}
                              {#key page}
                                <MenuItem circleDetails={circleDetails} index={LmsUtils.getMenuItemNumber(page, item.pages)} item={page} group={item}
                                          active={page.id === selectedPage?.id}
                                          {menuSize}
                                          on:options={(event) => showPageOptions(event.detail.target, page)}
                                          on:click={() => onClickMenuItem(page)} />
                              {/key}
                            {/each}

                            <a on:click={() => addPageFromCircle(item)}
                               class="f-left with-icon small-button blue circle-effect">
                              {M.ADD_PAGE}
                            </a>
                          </div>
                        {/if}
                      {/if}
                    </div>
                  {:else}
                    {#key item}
                      <MenuItem circleDetails={circleDetails} index={LmsUtils.getMenuItemNumber(item, orderedItems)} item={item}
                                active={item.id === selectedPage?.id}
                                {menuSize}
                                on:options={(event) => showPageOptions(event.detail.target, item)}
                                on:click={() => onClickMenuItem(item)} />
                    {/key}
                  {/if}
                {/each}

                <div class="col20 f-left mt-5 callapse-flex-center mb-25">
                  <a on:click={onAdd} class="middle-button blue with-icon expanded-menu-only">{M.ADD}</a>
                  <a on:click={onAdd} class="blue medium-circle-button compact-menu-only none-soft align-center justify-center rounded-effect">
                    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#5f6368">
                      <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" />
                    </svg>
                  </a>
                </div>
              </div>

              {#if selectedGroup && circleDetails.circle.styling.submenuStyle === 'replace'}
                <div class="col20 f-left circle-slide-submenu" transition:fly={{x: "100%", opacity: 1}}>
                  <div class="circle-page-item col20 f-left flex flex-col">
                    <h4 class="circle-item-name flex align-center space-between col20 f-left circle-group-header">
                      <span>{selectedGroup.pageGroup.name}</span>
                      <a class="circle-sidenav-back rounded-btn-effect flex align-center" on:click={closeSelectedGroup}>
                        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#000000">
                          <path d="M0 0h24v24H0z" fill="none"></path>
                          <path d="M21 11H6.83l3.58-3.59L9 6l-6 6 6 6 1.41-1.41L6.83 13H21z"></path>
                        </svg>
                        {M.BACK}
                      </a>
                    </h4>

                    {#if selectedGroupTrackedPages > 0}
                      <div class="progress-tracker col20 f-left mt-10 mb-10">
                        <p class="col20 f-left inner-circle-desc t-left mb-5">0/{selectedGroupTrackedPages} {M.COMPLETED}</p>
                        <div class="progress-bar">
                          <div class="success-bar primaryColor-bg2"></div>
                        </div>
                      </div>
                    {/if}
                  </div>

                  <div class="col20 f-left"
                       use:LmsUtils.setupMenuSortable={[circleDetails.circle, selectedGroup.pages, () => { selectedGroup.pages = selectedGroup.pages }]}>
                    {#each selectedGroup.pages as page, pageIdx (page)}
                      {#key page}
                        <MenuItem circleDetails={circleDetails} index={LmsUtils.getMenuItemNumber(page, selectedGroup.pages)} item={page}
                                  group={selectedGroup} on:click={() => onClickMenuItem(page)}
                                  active={page.id === selectedPage?.id}
                                  {menuSize}
                                  on:options={(event) => showPageOptions(event.detail.target, page)} />
                      {/key}
                    {/each}
                  </div>

                  

                  <div class="col20 f-left mt-5 callapse-flex-center">
                    <a on:click={() => addPageFromCircle(selectedGroup)}
                       class="f-left with-icon small-button blue circle-effect mb-15">
                      {M.ADD_PAGE}
                    </a>
                    <a on:click={() => addPageFromCircle(selectedGroup)} class="blue medium-circle-button compact-menu-only none-soft align-center justify-center rounded-effect">
                      <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#5f6368">
                        <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" />
                      </svg>
                    </a>
                  </div>

                </div>
              {/if}
            </div>
          </div>
        </div>
      </div>

      {#if selectedGroup && circleDetails.circle.styling.submenuStyle === 'sidebar' && circleDetails.circle.styling.sidebar.position === 'left'}
        <LmsCircleSidebar {selectedGroup} {circleDetails} {selectedPage} position="left" {menuSize}
                          on:addpage={() => addPageFromCircle(selectedGroup)}
                          on:itemclick={(event) => onClickMenuItem(event.detail.page)}
                          on:options={(event) => showPageOptions(event.detail.target, event.detail.page)} />
      {/if}

      {#if selectedPage}
        {#key selectedPage.id}
          <CirclePageDetails bind:this={pageDetailsInstance} {circleDetails} {selectedPage} {selectedGroup} {isTemplate}
                             on:change={() => setPageChanged(true, true)} on:settings={() => showPageSettingsFromCircle(selectedPage)}
                             on:publish={publishActivePage} />
        {/key}
      {:else}
        <div class="col20 f-left mt-50 mb-25 flex flex-col align-center">
          <span class="material-icons-outlined fingerprint-icon" style="background: #f5f6fa;"> bubble_chart </span>
          <h3 class="mt-25">{M.START_ADDING_PAGE}</h3>
          <a class="middle-button blue mt-25" on:click={() => addPageFromCircle(null)}>{M.ADD_PAGE}</a>
        </div>
      {/if}

      {#if selectedGroup && circleDetails.circle.styling.submenuStyle === 'sidebar' && circleDetails.circle.styling.sidebar.position === 'right'}
        <LmsCircleSidebar {selectedGroup} {circleDetails} {selectedPage} position="right" {menuSize}
                          on:addpage={() => addPageFromCircle(selectedGroup)}
                          on:itemclick={(event) => onClickMenuItem(event.detail.page)}
                          on:options={(event) => showPageOptions(event.detail.target, event.detail.page)} />
      {/if}
    </div>

    {#if !isTemplate && !circleDetails.circle.published}
      <div class="col20 f-left red-bg2 white-text circle-floating-controls-outer">
        <div class="circle-floating-controls flex flex-row align-center justify-center">
          <div class="flex align-center">
            <span class="inline-block">{M.THIS_CIRCLE_IS_HIDDEN}</span>
            <a on:click={togglePublishedState} class="small-button green rounded-effect">{M.PUBLISH}</a>
          </div>
        </div>
      </div>
    {/if}
  {/if}
</div>


<style>

  /** Circle Name Font & Size: **/

  .circle-name {
    font-family: var(--circleNameFont), sans-serif;
    font-size: calc(var(--circleNameTextSize) * 1px);
    color: var(--circleNameColor);
  }

  .circle-image {
    border-color: var(--navigationBackgroundColor);
  }

  .circle-image.circle-image-icon {
    background-color: var(--iconBg);
  }

  .circle-wrapper :global(.circle-sidebar .circle-image-icon svg) {
    fill: var(--iconFg);
  }

  :global(.circle-image-icon span) {
    color: var(--iconFg);
  }

  /** Navigation Background & Navigation Width **/
  :global(.circle-cover) {
    background-color: var(--circleCoverBackgroundColor);
  }

  :global(.circle-sidebar) {
    overflow-x: hidden;
    background-color: var(--navigationBackgroundColor);
    width: calc(var(--navigationWidth) * 1px);
  }

  .circle-slide-submenu {
    background-color: var(--navigationBackgroundColor);
    z-index: 100;
    position: absolute;
    top: 0;
  }

  :global(.circle-group-side-navigation) {
    background-color: var(--sidebarBgColor);
  }

  /** Navigation Text & Icon color: **/

  :global(
  .circle-members-info,
  .circle-sidebar .circle-description,
  .circle-item-name,
  .page-description,
  .inner-circle-desc,
  .circle-group-name
  ) {
    color: var(--navTextFg);
  }

  .circle-members-info,
  .circle-description {
    color: var(--circleNameColor);
  }

  :global(.circle-item-name) {
    font-family: var(--navPageFontFamily), sans-serif;
    font-size: var(--navPageFontSize);
  }

  /** Sidebar Navigation Text & Icon color: **/

  :global(
  .circle-group-side-navigation .circle-item-name,
  .circle-group-side-navigation .page-description,
  .circle-group-side-navigation .inner-circle-desc
  ) {
    color: var(--sidebarTextColor);
  }

  /* Icon Styling */

  :global(.circle-sidebar-content-wrapper .circle-icon svg),
  .circle-wrapper.circle-layout-2 .circle-navigation :global(.circle-video-play-btn svg) {
    fill: var(--menuIconFg);
  }

  :global(.circle-sidebar-content-wrapper .circle-icon),
  .circle-layout-2 :global(.circle-sidebar .circle-video-play-btn) {
    background-color: var(--menuIconBg);
    color: var(--menuIconFg);
    border: 0 none;
  }

  :global(.circle-sidebar-content-wrapper.icons-with-border .circle-icon) {
    border: 1px solid var(--menuIconBorder);
  }

  :global(.circle-sidebar-content-wrapper .inner-circle-toggle-icon),
  :global(.circle-sidebar-content-wrapper .circle-page-item .pending-icon) {
    fill: var(--navTextFg);
    outline-color: var(--navTextFg);
    border-color: var(--navTextFg);
  }

  :global(.circle-sidebar-content-wrapper .circle-sidenav-back) {
    border-color: var(--navTextFg);
    color: var(--navTextFg);
  }

  :global(.circle-sidebar-content-wrapper .circle-sidenav-back) svg {
    fill: var(--navTextFg);
  }

  /* Badge Styling */
  :global(.circle-sidebar-content-wrapper .circle-badge) {
    color: var(--badgeFg);
    background: var(--badgeBg);
    fill: var(--badgeBg);
  }

  :global(.circle-sidebar-content-wrapper .lock-icon) {
    fill: var(--badgeFg);
    background: var(--badgeBg);
  }

  /* Sidebar Icon Styling */

  :global(.circle-group-side-navigation .circle-icon svg) {
    fill: var(--sidebarIconFg);
  }

  :global(.circle-group-side-navigation .circle-icon) {
    background-color: var(--sidebarIconBg);
    color: var(--sidebarIconFg);
    border: 0 none;
  }

  :global(.circle-group-side-navigation.icons-with-border .circle-icon) {
    border: 1px solid var(--sidebarIconBorder);
  }

  :global(.circle-group-side-navigation .inner-circle-toggle-icon),
  :global(.circle-group-side-navigation .circle-page-item .pending-icon) {
    fill: var(--sidebarTextColor);
    border-color: var(--sidebarTextColor);
  }

  :global(.circle-group-side-navigation .circle-sidenav-back) {
    border-color: var(--sidebarTextColor);
    color: var(--sidebarTextColor);
  }

  :global(.circle-group-side-navigation .circle-sidenav-back) svg {
    fill: var(--sidebarTextColor);
  }

  /* Sidebar Badge Styling */
  :global(.circle-group-side-navigation .circle-badge) {
    color: var(--sidebarBadgeFg);
    background: var(--sidebarBadgeBg);
    fill: var(--sidebarBadgeBg);
  }

  :global(.circle-group-side-navigation .lock-icon) {
    fill: var(--sidebarBadgeFg);
    background: var(--sidebarBadgeBg);
  }
</style>
